import type { VaultApy, VaultApyComposite, VaultApyRange } from '@/types/apy'
import type { VaultRewards } from '@/types/rewards'
import type { Vault } from '@/types/vault'
import { getApyTypeKey } from '@/utils/apy'
import { cn } from '@/utils/cn'
import { checkActiveRewards } from '@/utils/vault/rewards'
import { formatDecimalToPercentage } from '@vaultsfyi/common/utils'
import { IntrinsicIcon } from '../icons/intrinsic'
import { RewardsIcon } from '../icons/rewards'
import { SunIcon } from '../icons/sun'
import { Tooltip, TooltipContent, type TooltipProps } from './tooltip'

interface ApyTooltipProps {
  asset: Vault['asset']
  apy: VaultApy
  apyRange: VaultApyRange
  rewards: VaultRewards
  additionalIncentives?: string
  apyComposite?: VaultApyComposite
  includeRewards: boolean
}

type Props = ApyTooltipProps & Pick<TooltipProps, 'className' | 'tabIndex'>

export const ApyTooltip = ({
  asset,
  apy,
  apyRange,
  rewards,
  additionalIncentives,
  apyComposite,
  includeRewards,
  className,
  tabIndex,
}: Props) => {
  const hasRewards = checkActiveRewards(rewards)
  if (!hasRewards && !additionalIncentives && !apyComposite) {
    return null
  }

  return (
    <Tooltip
      tabIndex={tabIndex}
      className={cn(
        'group flex min-h-6 w-fit min-w-6 items-center justify-center rounded-full border border-transparent bg-transparent px-0.5',
        'hocus:border-border hocus:bg-surface hocus:text-heading-foreground',
        className
      )}
    >
      <>
        {apyComposite && <IntrinsicIcon />}
        {hasRewards && <RewardsIcon />}
        {additionalIncentives && <SunIcon />}
      </>
      <TooltipContent className="w-full max-w-56 px-3 py-3">
        <div className="flex flex-col gap-2">
          <div className={tooltipRowStyles}>
            <div className={tooltipTitleStyles}>
              <span>Base</span>
            </div>
            <span className={tooltipValueStyles}>{formatDecimalToPercentage(apy[apyRange].base)}</span>
          </div>
          {apyComposite && (
            <div className={tooltipRowStyles}>
              <div className={tooltipTitleStyles}>
                <IntrinsicIcon />
                <span>Intrinsic ({asset.symbol})</span>
              </div>
              <span className={tooltipValueStyles}>
                {formatDecimalToPercentage(apyComposite.intrinsicApy[apyRange][getApyTypeKey({ includeRewards })])}
              </span>
            </div>
          )}
          {hasRewards && (
            <div className={tooltipRowStyles}>
              <div className={tooltipTitleStyles}>
                <RewardsIcon />
                <span>Rewards</span>
              </div>
              <span className={tooltipValueStyles}>{formatDecimalToPercentage(apy[apyRange].reward)}</span>
            </div>
          )}
          {additionalIncentives && (
            <div className={tooltipRowStyles}>
              <div className={tooltipTitleStyles}>
                <SunIcon />
                <span>Points</span>
              </div>
              <span className={tooltipValueStyles}>{additionalIncentives}</span>
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

const tooltipRowStyles = 'flex flex-wrap items-center gap-x-2 gap-y-1'
const tooltipTitleStyles = 'flex items-center gap-1'
const tooltipValueStyles = 'ml-auto font-bold'
